import type { InspectionInDto } from 'shared/dtos/in/inspection';
import type { ProtocolAnswerInDto } from 'shared/dtos/in/protocolAnswer';
import type { ProtocolItemInDto } from 'shared/dtos/in/protocolItem';
import type { ProtocolSectionInDto } from 'shared/dtos/in/protocolSection';
import type { ProtocolSectionAnswersBatchResult } from 'shared/dtos/out/protocolAnswer';
import { isPlainObject } from 'shared/utils/object';

/**
 * Narrows the `updatedEntity` outcome of an inspection change to what that
 * change's server answer is. The guards check the fields that tell the answers
 * apart, so a wrong answer fails the change instead of corrupting the view.
 */

type Outcome = Record<string, unknown>;

const hasId = (outcome: Outcome): boolean => typeof outcome._id === 'string';

export function isInspectionOutcome(outcome: unknown): outcome is InspectionInDto {
  return isPlainObject(outcome) && hasId(outcome) && Array.isArray(outcome.protocol);
}

export function isProtocolItemOutcome(outcome: unknown): outcome is ProtocolItemInDto {
  return isPlainObject(outcome) && hasId(outcome) && typeof outcome.templateChecklistItem === 'string';
}

export function isProtocolAnswerOutcome(outcome: unknown): outcome is ProtocolAnswerInDto {
  return isPlainObject(outcome) && hasId(outcome) && typeof outcome.checklistAnswerId === 'string';
}

/**
 * The server answers a section change with `_id`, `checklistSectionId`, `comment`
 * and `documents` only (no `title` or `order`), hence `Partial`.
 */
export function isProtocolSectionOutcome(outcome: unknown): outcome is Partial<ProtocolSectionInDto> {
  return (
    isPlainObject(outcome) &&
    hasId(outcome) &&
    typeof outcome.comment === 'string' &&
    Array.isArray(outcome.documents) &&
    !Array.isArray(outcome.protocol)
  );
}

export function isProtocolSectionAnswersOutcome(outcome: unknown): outcome is ProtocolSectionAnswersBatchResult {
  return isPlainObject(outcome) && typeof outcome.isApplicable === 'boolean' && Array.isArray(outcome.protocolItemIds);
}

/** Returns the outcome when it is what the change expects; throws otherwise. */
export function expectOutcome<TOutcome>(
  outcome: unknown,
  isExpected: (outcome: unknown) => outcome is TOutcome,
  change: string
): TOutcome {
  if (!isExpected(outcome)) {
    throw new Error(`Unexpected outcome of the inspection change: ${change}`);
  }
  return outcome;
}
